/**
 * Wishlist Feature — mecindo.no
 * localStorage-first implementation with sync-ready architecture.
 *
 * Data model: localStorage key 'mecindo-wishlist' stores an array of product handle strings.
 * All UI components listen for the 'wishlist:updated' CustomEvent on document.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'mecindo-wishlist';

  // ─── Core Data API ────────────────────────────────────────────────

  var Wishlist = {
    _items: null,

    _load: function () {
      if (this._items !== null) return this._items;
      try {
        this._items = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
      } catch (e) {
        this._items = [];
      }
      return this._items;
    },

    _save: function () {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this._items));
      } catch (e) {
        // Storage full or unavailable
      }
      document.dispatchEvent(new CustomEvent('wishlist:updated', {
        detail: { items: this._items.slice(), count: this._items.length }
      }));
    },

    add: function (handle) {
      this._load();
      if (!handle || this._items.indexOf(handle) !== -1) return;
      this._items.push(handle);
      this._save();
    },

    remove: function (handle) {
      this._load();
      var idx = this._items.indexOf(handle);
      if (idx === -1) return;
      this._items.splice(idx, 1);
      this._save();
    },

    toggle: function (handle) {
      this._load();
      if (this._items.indexOf(handle) !== -1) {
        this.remove(handle);
        return false; // was removed
      } else {
        this.add(handle);
        return true; // was added
      }
    },

    has: function (handle) {
      this._load();
      return this._items.indexOf(handle) !== -1;
    },

    getAll: function () {
      this._load();
      return this._items.slice();
    },

    count: function () {
      this._load();
      return this._items.length;
    },

    clear: function () {
      this._items = [];
      this._save();
    }
  };

  // Expose globally for external access / future sync layer
  window.Wishlist = Wishlist;

  // ─── Toast Notification ───────────────────────────────────────────

  var toastEl = null;
  var toastTimer = null;

  function showToast(message) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'wishlist-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    clearTimeout(toastTimer);
    // Force reflow to restart animation
    toastEl.classList.remove('is-visible');
    void toastEl.offsetWidth;
    toastEl.classList.add('is-visible');
    toastTimer = setTimeout(function () {
      toastEl.classList.remove('is-visible');
    }, 2500);
  }

  // ─── Tracking helpers (Hello Retail + GA dataLayer) ───────────────

  function readProductData(el) {
    var price = parseFloat(el.getAttribute('data-product-price'));
    return {
      id: el.getAttribute('data-product-id') || '',
      sku: el.getAttribute('data-product-sku') || '',
      variant: el.getAttribute('data-product-variant') || '',
      title: el.getAttribute('data-product-title') || '',
      price: isNaN(price) ? 0 : price,
      currency: el.getAttribute('data-product-currency') || '',
      url: el.getAttribute('data-product-url') || ''
    };
  }

  function trackHelloRetailWishlistClick(data) {
    if (!data.id) return;
    try {
      var url = data.url || null;
      // Hello Retail expects a canonical absolute URL — product.url renders as
      // a relative path in Liquid, so resolve to origin here.
      if (url && url.charAt(0) === '/') {
        url = window.location.origin + url;
      }
      window.hrq = window.hrq || [];
      window.hrq.push(['trackPageView', url, String(data.id)]);
    } catch (e) { /* best effort */ }
  }

  function pushAddToWishlistEvent(data) {
    if (!data.id && !data.sku) return;
    try {
      var item = {
        item_id: String(data.id),
        item_name: data.title,
        price: data.price
      };
      if (data.sku) item.item_sku = data.sku;
      if (data.variant) item.item_variant = String(data.variant);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'add_to_wishlist',
        ecommerce: {
          currency: data.currency,
          value: data.price,
          items: [item]
        }
      });
    } catch (e) { /* best effort */ }
  }

  // ─── <wishlist-button> Web Component ──────────────────────────────

  var WishlistButton = (function () {
    function WishlistButtonElement() {
      var el = Reflect.construct(HTMLElement, [], WishlistButtonElement);
      return el;
    }

    WishlistButtonElement.prototype = Object.create(HTMLElement.prototype);
    WishlistButtonElement.prototype.constructor = WishlistButtonElement;

    WishlistButtonElement.prototype.connectedCallback = function () {
      var self = this;
      this._handle = this.getAttribute('data-product-handle');
      this._btn = this.querySelector('.wishlist-btn__toggle');

      // Set initial state
      if (Wishlist.has(this._handle)) {
        this.classList.add('is-active');
      }

      // Click handler — bind only once. The custom element can re-enter the DOM
      // (carousel re-renders, HR recommendations moving <li> nodes around), and
      // each connectedCallback invocation would otherwise stack another listener
      // on the inner button, causing tracking to fire 2x, 3x, ... per click.
      if (this._btn && !this._btn._wishlistClickBound) {
        this._btn._wishlistClickBound = true;
        this._btn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          var wasAdded = Wishlist.toggle(self._handle);
          if (wasAdded) {
            self.classList.add('is-active', 'is-animating');
            showToast('Tilføjet til dine favoritter');
            setTimeout(function () {
              self.classList.remove('is-animating');
            }, 400);

            var productData = readProductData(self);
            trackHelloRetailWishlistClick(productData);
            pushAddToWishlistEvent(productData);
          } else {
            self.classList.remove('is-active');
            showToast('Fjernet fra dine favoritter');
          }
        });
      }

      // Listen for external updates (e.g. removed from wishlist page)
      this._updateHandler = function () {
        if (Wishlist.has(self._handle)) {
          self.classList.add('is-active');
        } else {
          self.classList.remove('is-active');
        }
      };
      document.addEventListener('wishlist:updated', this._updateHandler);
    };

    WishlistButtonElement.prototype.disconnectedCallback = function () {
      if (this._updateHandler) {
        document.removeEventListener('wishlist:updated', this._updateHandler);
      }
    };

    return WishlistButtonElement;
  })();

  if (!customElements.get('wishlist-button')) {
    customElements.define('wishlist-button', WishlistButton);
  }

  // ─── <wishlist-page> Web Component ────────────────────────────────

  var WishlistPage = (function () {
    function WishlistPageElement() {
      var el = Reflect.construct(HTMLElement, [], WishlistPageElement);
      return el;
    }

    WishlistPageElement.prototype = Object.create(HTMLElement.prototype);
    WishlistPageElement.prototype.constructor = WishlistPageElement;

    WishlistPageElement.prototype.connectedCallback = function () {
      var self = this;

      // ── Delt visning: ?handles=a,b,c i URL ─────────────────────
      var urlParams = new URLSearchParams(window.location.search);
      var sharedParam = urlParams.get('handles');
      if (sharedParam) {
        // Sikkerhed: max 100 handles, bloker farlige tegn (men tillad æøå m.fl.), max 255 tegn pr. handle
        var MAX_SHARED = 100;
        var INVALID_HANDLE = /[\s<>"'&\/\\,]/;  // blokér XSS, path traversal og komma
        var sharedHandles = sharedParam
          .split(',')
          .map(function (h) { return h.trim().toLowerCase(); })
          .filter(function (h) { return h.length > 0 && h.length <= 255 && !INVALID_HANDLE.test(h); })
          .slice(0, MAX_SHARED);
        this._renderShared(sharedHandles);
        return;
      }

      // ── Normal visning: brukerens egen ønskeliste ────────────────
      this._knownHandles = Wishlist.getAll();
      this._render();
      document.addEventListener('wishlist:updated', function (e) {
        var newHandles = (e.detail && e.detail.items) ? e.detail.items : Wishlist.getAll();
        var oldHandles = self._knownHandles;
        var removed = oldHandles.filter(function (h) { return newHandles.indexOf(h) === -1; });
        self._knownHandles = newHandles.slice();

        // If items were removed and all of them are cards on this page,
        // animate them out instead of re-rendering the whole grid.
        var allOnPage = removed.length > 0 && removed.every(function (h) {
          return self.querySelector('.wishlist-grid-item[data-handle="' + h + '"]');
        });

        if (allOnPage) {
          removed.forEach(function (h) {
            var card = self.querySelector('.wishlist-grid-item[data-handle="' + h + '"]');
            if (card) card.classList.add('is-removing');
          });
          setTimeout(function () {
            removed.forEach(function (h) {
              var card = self.querySelector('.wishlist-grid-item[data-handle="' + h + '"]');
              if (card) card.remove();
            });
            self._updateCount();
          }, 300);
          return;
        }

        self._render();
      });
    };

    // ── Delt ønskeliste — read-only visning med samtykke-CTA ────────

    WishlistPageElement.prototype._renderShared = function (handles) {
      var self = this;

      // Skjul del-knapp (ikke relevant i delt visning)
      var shareUi = document.getElementById('wishlist-share-ui');
      if (shareUi) shareUi.hidden = true;

      // Oppdater produktteller
      var countEl = this.querySelector('.wishlist-page__count');
      if (countEl) {
        countEl.textContent = '(' + handles.length + (handles.length === 1 ? ' produkt' : ' produkter') + ')';
      }

      // Bygg banner med CTA
      var alreadyAll = handles.length > 0 && handles.every(function (h) { return Wishlist.has(h); });
      var banner = document.createElement('div');
      banner.className = 'wishlist-shared-banner';
      banner.innerHTML =
        '<div class="wishlist-shared-banner__info">' +
          '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
            '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>' +
          '</svg>' +
          '<span>' + handles.length + (handles.length === 1 ? ' favorit er delt med dig' : ' favoritter er delt med dig') + '</span>' +
        '</div>' +
        (alreadyAll
          ? '<span class="wishlist-shared-banner__done">Allerede i dine favoritter \u2713</span>'
          : '<button class="wishlist-shared-banner__btn" type="button">Gem til dine favoritter</button>');
      this.insertBefore(banner, this.firstChild);

      // CTA-klik: tilføj direkte til localStorage, vis toast
      var btn = banner.querySelector('.wishlist-shared-banner__btn');
      if (btn) {
        btn.addEventListener('click', function () {
          btn.disabled = true;
          var added = 0;
          handles.forEach(function (handle) {
            if (!Wishlist.has(handle)) {
              Wishlist.add(handle);
              added++;
            }
          });
          // Rens URL uden reload
          window.history.replaceState({}, '', window.location.pathname);
          // Toast direkte
          showToast(added + (added === 1
            ? ' produkt er tilføjet til dine favoritter'
            : ' produkter er tilføjet til dine favoritter'));
          // Opdater knappen til succes-tilstand
          btn.textContent = 'Gemt til dine favoritter ✓';
          btn.classList.add('wishlist-shared-banner__btn--done');
        });
      }

      // Render produkterne
      var gridEl = this.querySelector('.wishlist-page__grid');
      var emptyEl = this.querySelector('.wishlist-empty');

      if (handles.length === 0) {
        if (gridEl) { gridEl.innerHTML = ''; gridEl.style.display = 'none'; }
        if (emptyEl) emptyEl.style.display = 'block';
        return;
      }

      if (emptyEl) emptyEl.style.display = 'none';
      if (gridEl) gridEl.style.display = '';
      this._loadProducts(handles, gridEl);
    };

    WishlistPageElement.prototype._updateCount = function () {
      var handles = Wishlist.getAll();
      var countEl = this.querySelector('.wishlist-page__count');
      if (countEl) {
        countEl.textContent = '(' + handles.length + (handles.length === 1 ? ' produkt' : ' produkter') + ')';
      }
      // Show empty state if no items left
      if (handles.length === 0) {
        var gridEl = this.querySelector('.wishlist-page__grid');
        var emptyEl = this.querySelector('.wishlist-empty');
        if (gridEl) { gridEl.innerHTML = ''; gridEl.style.display = 'none'; }
        if (emptyEl) emptyEl.style.display = 'block';
      }
    };

    WishlistPageElement.prototype._render = function () {
      var handles = Wishlist.getAll();
      var countEl = this.querySelector('.wishlist-page__count');
      if (countEl) {
        countEl.textContent = '(' + handles.length + (handles.length === 1 ? ' produkt' : ' produkter') + ')';
      }

      var gridEl = this.querySelector('.wishlist-page__grid');
      var emptyEl = this.querySelector('.wishlist-empty');

      if (handles.length === 0) {
        if (gridEl) gridEl.innerHTML = '';
        if (gridEl) gridEl.style.display = 'none';
        if (emptyEl) emptyEl.style.display = 'block';
        return;
      }

      if (emptyEl) emptyEl.style.display = 'none';
      if (gridEl) gridEl.style.display = '';

      this._loadProducts(handles, gridEl);
    };

    WishlistPageElement.prototype._loadProducts = function (handles, gridEl) {
      if (!gridEl) return;

      // Show skeleton
      gridEl.innerHTML = this._buildSkeleton(handles.length);

      var self = this;
      var cards = [];
      var loaded = 0;

      handles.forEach(function (handle) {
        fetch('/products/' + handle + '?section_id=wishlist-card')
          .then(function (res) {
            if (!res.ok) throw new Error('Not found');
            return res.text();
          })
          .then(function (html) {
            cards.push({ handle: handle, html: html });
          })
          .catch(function () {
            // Product not found or error — skip it
          })
          .finally(function () {
            loaded++;
            if (loaded === handles.length) {
              // Sort cards to match wishlist order
              var sorted = [];
              handles.forEach(function (h) {
                var found = cards.find(function (c) { return c.handle === h; });
                if (found) sorted.push(found);
              });
              self._renderCards(sorted, gridEl);
            }
          });
      });
    };

    WishlistPageElement.prototype._buildSkeleton = function (count) {
      var html = '';
      for (var i = 0; i < count; i++) {
        html +=
          '<div class="wishlist-skeleton__card">' +
            '<div class="wishlist-skeleton__image"></div>' +
            '<div class="wishlist-skeleton__text">' +
              '<div class="wishlist-skeleton__line"></div>' +
              '<div class="wishlist-skeleton__line"></div>' +
              '<div class="wishlist-skeleton__line"></div>' +
              '<div class="wishlist-skeleton__line"></div>' +
            '</div>' +
          '</div>';
      }
      return html;
    };

    WishlistPageElement.prototype._renderCards = function (cards, gridEl) {
      if (cards.length === 0) {
        gridEl.innerHTML = '';
        gridEl.style.display = 'none';
        var emptyEl = this.querySelector('.wishlist-empty');
        if (emptyEl) emptyEl.style.display = 'block';
        return;
      }

      var html = '';
      var self = this;

      cards.forEach(function (item) {
        html +=
          '<div class="wishlist-grid-item" data-handle="' + self._escapeHtml(item.handle) + '">' +
            item.html +
          '</div>';
      });

      gridEl.innerHTML = html;
    };

    WishlistPageElement.prototype._formatMoney = function (price) {
      // Shopify product JSON returns price as a string like "499.00" (already in base currency)
      var amount = parseFloat(price);
      if (isNaN(amount)) return '';
      // Norwegian format: whole number for round prices, one decimal if .X0, two decimals otherwise
      var formatted;
      if (amount === Math.floor(amount)) {
        formatted = Math.floor(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      } else {
        var parts = amount.toFixed(2).split('.');
        var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        formatted = whole + ',' + parts[1];
      }
      return formatted + ' kr';
    };

    WishlistPageElement.prototype._resizeImage = function (src, size) {
      if (!src) return '';
      // Shopify image URL resizing
      return src.replace(/\.([a-z]+)(\?|$)/, '_' + size + '.$1$2');
    };

    WishlistPageElement.prototype._escapeHtml = function (str) {
      if (!str) return '';
      var div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    };

    return WishlistPageElement;
  })();

  if (!customElements.get('wishlist-page')) {
    customElements.define('wishlist-page', WishlistPage);
  }

  // ─── Header Badge Updater ─────────────────────────────────────────

  function updateHeaderBadge() {
    var count = Wishlist.count();
    var badge = document.getElementById('wishlist-count');
    if (!badge) return;

    if (count > 0) {
      badge.innerHTML = '<span aria-hidden="true">' + count + '</span>';
      badge.hidden = false;
    } else {
      badge.innerHTML = '';
      badge.hidden = true;
    }
  }

  // Initial badge update on load
  document.addEventListener('DOMContentLoaded', updateHeaderBadge);
  document.addEventListener('wishlist:updated', updateHeaderBadge);

  // ─── Header Count Bubble ──────────────────────────────────────────
  function updateWishlistBubble() {
    var bubble = document.getElementById('wishlist-count-bubble');
    if (!bubble) return;
    var count = Wishlist.getAll().length;
    if (count > 0) {
      bubble.textContent = count;
      bubble.style.display = '';
    } else {
      bubble.style.display = 'none';
    }
  }

  // Update on load and on every change
  updateWishlistBubble();
  document.addEventListener('wishlist:updated', updateWishlistBubble);


  function getShareUrl() {
    var handles = Wishlist.getAll();
    if (handles.length === 0) return null;
    return window.location.origin + window.location.pathname + '?handles=' + handles.join(',');
  }

  function updateShareLinks(url) {
    var encodedUrl = encodeURIComponent(url);

    var emailLink = document.getElementById('wishlist-share-email');
    if (emailLink) {
      emailLink.href =
        'mailto:?subject=' + encodeURIComponent('Mine favoritter') +
        '&body=' + encodeURIComponent('Hej!\n\nSe de produkter jeg ønsker mig:\n' + url);
    }

    var messengerLink = document.getElementById('wishlist-share-messenger');
    if (messengerLink) {
      var isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      messengerLink.href = isMobile
        ? 'fb-messenger://share?link=' + encodedUrl
        : 'https://www.messenger.com/';
        if (!isMobile) messengerLink.title = 'Åbn Messenger og del linket manuelt';
    }
  }

  function initShareUi() {
    var trigger = document.getElementById('wishlist-share-trigger');
    var dropdown = document.getElementById('wishlist-share-dropdown');
    if (!trigger || !dropdown) return;

    function updateShareVisibility() {
      trigger.hidden = Wishlist.count() === 0;
    }
    updateShareVisibility();
    document.addEventListener('wishlist:updated', updateShareVisibility);

    trigger.addEventListener('click', function (e) {
      e.stopPropagation();
      var url = getShareUrl();
      if (!url) return;

      // Mobil med Web Share API: brug native delingsmenu
      if (navigator.share && window.matchMedia('(max-width: 768px)').matches) {
        navigator.share({
          title: 'Mine favoritter',
          text: 'Se de produkter jeg ønsker mig!',
          url: url
        }).catch(function () {});
        return;
      }

      var isOpen = trigger.getAttribute('aria-expanded') === 'true';
      if (isOpen) {
        trigger.setAttribute('aria-expanded', 'false');
        dropdown.hidden = true;
      } else {
        trigger.setAttribute('aria-expanded', 'true');
        dropdown.hidden = false;
        updateShareLinks(url);
      }
    });

    document.addEventListener('click', function () {
      trigger.setAttribute('aria-expanded', 'false');
      dropdown.hidden = true;
    });

    dropdown.addEventListener('click', function (e) {
      e.stopPropagation();
    });

    // Kopier lenke
    var copyBtn = document.getElementById('wishlist-share-copy');
    var copyLabel = copyBtn && copyBtn.querySelector('.wishlist-share__label');
    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        var url = getShareUrl();
        if (!url) return;

        var doConfirm = function () {
          copyBtn.classList.add('wishlist-share__option--copied');
          if (copyLabel) copyLabel.textContent = 'Kopiert ✓';
          setTimeout(function () {
            copyBtn.classList.remove('wishlist-share__option--copied');
            if (copyLabel) copyLabel.textContent = 'Kopier lenke';
            trigger.setAttribute('aria-expanded', 'false');
            dropdown.hidden = true;
          }, 1800);
        };

        if (navigator.clipboard) {
          navigator.clipboard.writeText(url).then(doConfirm).catch(function () {
            showToast('Link kopieret!');
            trigger.setAttribute('aria-expanded', 'false');
            dropdown.hidden = true;
          });
        } else {
          var ta = document.createElement('textarea');
          ta.value = url;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.focus();
          ta.select();
          try { document.execCommand('copy'); } catch (err) { /* best effort */ }
          document.body.removeChild(ta);
          doConfirm();
        }
      });
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    initShareUi();
  });

  // ─── Future Sync Hook ─────────────────────────────────────────────
  // When ready to add server sync, add a syncWithServer() method here.
  // It would be called on DOMContentLoaded and after each add/remove.
  // No other files need to change.

})();
